import { useState } from 'react';
import { useShopStore } from '../store/useShopStore';

export default function Toolbar() {
  const {
    zoom,
    layout,
    saveLayout,
    exportLayout,
    loadFromStorage,
    clearLayout,
    setLayoutName,
    canUndo,
    canRedo,
    undo,
    redo,
  } = useShopStore();
  
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="bg-gray-800 border-b border-gray-700 px-4 py-1.5 flex items-center justify-between">
      {/* Left: Layout name and undo/redo */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={layout.name}
          onChange={(e) => setLayoutName(e.target.value)}
          className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm w-40 focus:border-indigo-500 focus:outline-none"
          placeholder="Layout name..."
        />
        
        {/* Undo/Redo */}
        <div className="flex gap-0.5">
          <button
            onClick={undo}
            disabled={!canUndo()}
            title="Undo (Ctrl+Z)"
            className={`p-1.5 rounded transition-colors ${
              canUndo() 
                ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                : 'text-gray-600 cursor-not-allowed'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>
          <button
            onClick={redo}
            disabled={!canRedo()}
            title="Redo (Ctrl+Y)"
            className={`p-1.5 rounded transition-colors ${
              canRedo() 
                ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                : 'text-gray-600 cursor-not-allowed'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Right: Status and menu */}
      <div className="flex items-center gap-3">
        <span className="text-gray-500 text-xs">
          {layout.entities.length} • {Math.round(zoom * 100)}%
        </span>
        
        {/* Menu dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
            title="Menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          {showMenu && (
            <>
              {/* Backdrop to close menu */}
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowMenu(false)} 
              />
              
              {/* Menu */}
              <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-1 min-w-[140px] z-20">
                <button
                  onClick={() => { saveLayout(); setShowMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Save
                  <span className="text-gray-500 text-xs ml-auto">⌘S</span>
                </button>
                <button
                  onClick={() => { loadFromStorage(); setShowMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Load
                </button>
                <button
                  onClick={() => { exportLayout(); setShowMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export JSON
                </button>
                <div className="border-t border-gray-700 my-1" />
                <button
                  onClick={() => { clearLayout(); setShowMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-400 hover:bg-red-600/20 hover:text-red-400 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Clear All
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
